import React, { useEffect, useState } from 'react'
import Grid from '@mui/material/Grid'
import {
    differenceInDays,
    eachDayOfInterval,
    endOfDay,
    endOfQuarter,
    format,
    formatDate,
    isAfter,
    isBefore,
    startOfQuarter,
    subDays,
    subYears,
} from 'date-fns'
import { EnrichedIssueEntity } from 'types/IssueEntity'
import { useTranslation } from 'react-i18next'
import { useAtom } from 'jotai'
import { DataGridPro } from '@mui/x-data-grid-pro/DataGridPro'
import { getMuiDataGridLocaleText, useAppLocale } from '../../../i18n/localeUtils'
import Typography from '@mui/material/Typography'
import { useTheme } from '@mui/material/styles'
import { formatNumber, formatPercentage } from '../../../packages/utils/numberFormatters'
import { issueFiltersAtom, IssueFilterType } from '../../../atoms/issueAtom'
import { TimeLineChart } from '../../../components/TimeLineChart'
import { LineSeriesType } from '@mui/x-charts/models'
import {
    getCommitmentForLatestIssuesAtDate,
    getCommitmentExclGuarantorsForLatestIssuesAtDate,
    exportDataGridProps,
} from './utils'

const getMapKey = (d: Date) => format(d, 'yyyy-MM-dd')

type MetricType = 'ongoing-issues' | 'commitment' | 'commitment-excl-guarantors'

const calcStatsForOngoingIssues = (issues: EnrichedIssueEntity[], startDate: Date, endDate: Date) => {
    endDate = endOfDay(endDate)

    const daysInterval = eachDayOfInterval({ start: startDate, end: endDate })
    const daysMap = new Map(daysInterval.map((d) => [getMapKey(d), 0]))

    const fIssues = issues.filter((issue) => issue.publicationDate !== null)

    for (const issue of fIssues) {
        const pubDate = new Date(issue.publicationDate as string)
        const subEndDate = new Date(issue.subEndDate || endDate)
        if (isBefore(subEndDate, startDate)) continue
        if (isAfter(pubDate, endDate)) continue

        const dates = eachDayOfInterval({
            start: isAfter(pubDate, startDate) ? pubDate : startDate,
            end: isBefore(subEndDate, endDate) ? subEndDate : endDate,
        })

        for (const date of dates) {
            const key = getMapKey(date)

            daysMap.set(key, (daysMap.get(key) || 0) + 1)
        }
    }

    return {
        seriesData: Array.from(daysMap).map(([, count]) => count),
        xAxisData: daysInterval,
    }
}

const calcStatsForIssueMetrics = (
    issues: EnrichedIssueEntity[],
    metricType: Exclude<MetricType, 'ongoing-issues'>,
    startDate: Date,
    endDate: Date,
) => {
    const daysInterval = eachDayOfInterval({ start: startDate, end: endDate })

    return {
        seriesData:
            metricType === 'commitment'
                ? getCommitmentForLatestIssuesAtDate(issues, daysInterval)
                : getCommitmentExclGuarantorsForLatestIssuesAtDate(issues, daysInterval),
        xAxisData: daysInterval,
    }
}

export const OngoingIssuesStats = ({
    daysCount,
    graphSelector,
    hasComparison,
    hasTable,
    isLoading,
    issues,
    metricType,
}: {
    daysCount?: number
    graphSelector?: React.ReactNode
    hasComparison?: boolean
    hasTable?: boolean
    isLoading: boolean
    issues: EnrichedIssueEntity[]
    metricType: MetricType
}) => {
    const {
        t,
        i18n: { language: locale },
    } = useTranslation()
    const { dateFnsLocale } = useAppLocale()
    const theme = useTheme()
    const [filters] = useAtom(issueFiltersAtom)
    const [oldFilter, setOldFilter] = useState<IssueFilterType | null>(null)
    const [, setFilters] = useAtom(issueFiltersAtom)
    const [seriesData, setSeriesData] = useState<Array<number[]>>([])
    const [xAxisData, setXAxisData] = useState<Date[]>([])
    const [isCalculating, setIsCalculating] = useState(true)

    useEffect(() => {
        if (!issues) return
        setIsCalculating(true)

        try {
            const startDates =
                hasComparison && daysCount
                    ? [0, 1].map((yearCount) => {
                          const endDate = subYears(new Date(), yearCount)
                          const startDate = subDays(endDate, daysCount)

                          return { endDate, startDate }
                      })
                    : [
                          {
                              startDate: filters.dates.value[0],
                              endDate: new Date(),
                          },
                      ]

            const chartData = startDates.map(({ startDate, endDate }) => {
                if (metricType === 'ongoing-issues') {
                    return calcStatsForOngoingIssues(issues, startDate, endDate)
                }

                return calcStatsForIssueMetrics(issues, metricType, startDate, endDate)
            })

            setSeriesData(chartData.map((c) => c.seriesData))
            setXAxisData(chartData[0].xAxisData)
        } catch (err) {
            console.error(err)
        } finally {
            setIsCalculating(false)
        }
    }, [daysCount, filters.dates.value, hasComparison, issues, metricType])

    useEffect(() => {
        setFilters((prevFilters) => {
            if (!oldFilter) setOldFilter(prevFilters)

            return {
                ...prevFilters,
                issueTypes: {
                    value: ['Rights issue'],
                    disabledItems: ['Rights issue', 'Directed issue', 'IPO', 'Non-cash issue'],
                },
            }
        })

        return () => {
            if (oldFilter) setFilters((filters) => ({ ...filters, issueTypes: { ...oldFilter.issueTypes } }))
        }
    }, [setFilters, setOldFilter, oldFilter])

    const series: LineSeriesType[] = seriesData.map((d, index) => ({
        type: 'line',
        data: d,
        color: index === 0 ? theme.palette.FE.main : theme.palette.FE[900],
        label: !hasComparison
            ? t('charts.legend.futureAndOngoingRightsIssues')
            : index === 0
              ? t('charts.legend.currentPeriod')
              : t('charts.legend.oneYearAgo'),
        ids: index === 0 ? 'current' : 'lastYear',
        valueFormatter: (v) =>
            v === null
                ? null
                : metricType === 'ongoing-issues'
                  ? `${formatNumber(v, { decimals: 0, locale })} ${t('charts.units.st')}`
                  : `${formatPercentage(v, { decimals: 1, locale })}`,
    }))

    const columns = [
        {
            field: 'quarter',
            headerName: t('charts.table.quarter'),
        },
        {
            field: 'count',
            headerName: t('charts.table.countAverage'),
            flex: 1,
        },
    ]

    const groupedByQuarter = xAxisData.reduce(
        (acc, date, index) => {
            const quarter = formatDate(date, 'yyyy QQQ', { locale: dateFnsLocale })

            return {
                ...acc,
                [quarter]: {
                    daysInQuarter:
                        acc[quarter]?.daysInQuarter ||
                        differenceInDays(
                            Math.min(new Date().getTime(), endOfQuarter(date).getTime()),
                            startOfQuarter(date),
                        ),
                    totalCount: (acc[quarter]?.totalCount || 0) + seriesData[0][index],
                },
            }
        },
        {} as Record<string, { daysInQuarter: number; totalCount: number }>,
    )
    const tableRows = Object.keys(groupedByQuarter).map((quarter) => ({
        quarter,
        id: quarter,
        count: formatNumber(groupedByQuarter[quarter].totalCount / groupedByQuarter[quarter].daysInQuarter, {
            decimals: 0,
            locale,
        }),
    }))

    return (
        <Grid container spacing={10}>
            <Grid size={hasTable ? { xs: 12, md: 6 } : 12}>
                {graphSelector ? (
                    <Grid container>
                        <Grid size={12}>{graphSelector}</Grid>
                    </Grid>
                ) : null}
                <TimeLineChart
                    hasComparison={hasComparison}
                    series={series}
                    isLoading={isLoading || isCalculating}
                    xAxisData={xAxisData}
                />
            </Grid>
            {hasTable && (
                <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant='h6' gutterBottom>
                        {t('charts.table.count')}
                    </Typography>
                    <DataGridPro
                        {...exportDataGridProps}
                        columns={columns}
                        loading={isLoading || isCalculating}
                        localeText={getMuiDataGridLocaleText(locale)}
                        rows={tableRows}
                        hideFooter
                    />
                </Grid>
            )}
        </Grid>
    )
}
